import React, { FC, useContext, useMemo, useState } from 'react';
import { SearchInput, ReactTableContext, PageSpinner, ReactTableContextProvider } from '@td-client-frontend/common';
import { Button } from '../Button';
import {
  GetAllMarketingProfilesRes,
  MarketingProfile,
  useGetAllMarketingProfiles
} from '../../hooks/useMarketingProfile';
import MarketingProfilesTable from './components/MarketingProfilesTable';

export const MarketingProfiles: FC = () => {
  const [searchValue, setSearchValue] = useState<string>('');
  const [searchName, setSearchName] = useState<string>('');

  const {
    state: { sortBy }
  } = useContext(ReactTableContext);

  const { data, isLoading, fetchNextPage, isFetchingNextPage, hasNextPage } = useGetAllMarketingProfiles({
    name: searchName,
    mode: sortBy[0]?.desc ? 'DESC' : 'ASC',
    sortBy: sortBy[0]?.id || ''
  });

  const normalizedProfiles: MarketingProfile[] = useMemo(() => {
    if (!data?.pages.length || data.pages[0] === undefined) return [];
    return data.pages.reduce((pageAcc: MarketingProfile[], page: GetAllMarketingProfilesRes) => {
      const normalizedProfile: MarketingProfile[] = page.profiles?.reduce(
        (acc: MarketingProfile[], profile: { node: MarketingProfile }) => {
          return [
            ...acc,
            {
              ...profile.node,
              default: profile.node.default ? 'Yes' : ''
            }
          ];
        },
        []
      );
      return [...pageAcc, ...normalizedProfile];
    }, []);
  }, [data]);

  const onSearchHandler = () => {
    if (searchValue.length >= 3) {
      setSearchName(searchValue);
    }
  };

  const onEditSearchInput = (value: string) => {
    setSearchValue(value);
    if (value.length < 3) {
      setSearchName('');
    }
  };

  return (
    <div className="flex flex-col flex-1 overflow-hidden w-full px-7 pt-10 mx-auto bg-white font-effra">
      <h1 className="text-4xl font-effra mb-8">Marketing Profiles</h1>
      <div className="w-2/5 h-20 mt-8 flex">
        <SearchInput
          name="profiles"
          searchValue={searchValue}
          onChange={onEditSearchInput}
          label="Search profiles"
          searchInputWrapperClasses="w-full"
          closeCircleClasses="w-5 h-5"
          inputClasses="font-effra text-lg"
          labelClasses="text-lg text-gray-60 mb-2 font-normal font-effra"
        />
        <div className="ml-8 pb-1 h-fit flex items-end justify-center">
          <Button onClick={onSearchHandler} disabled={searchValue.length < 3} className="h-10 py-3.5">
            Search
          </Button>
        </div>
      </div>
      <div className="mt-12">
        <MarketingProfilesTable isFetching={isLoading} data={normalizedProfiles} searchName={searchName} />
        {hasNextPage && !isLoading && (
          <div className="w-full flex items-center justify-center py-4 text-lg">
            {isFetchingNextPage ? (
              <PageSpinner />
            ) : (
              <button
                onClick={() => fetchNextPage()}
                className="text-primary-500 font-effra text-lg"
                data-testid="load-more-profiles"
              >
                Load more
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export const MarketingProfileWithContext = (props: any) => {
  return (
    <ReactTableContextProvider>
      <MarketingProfiles {...props} />
    </ReactTableContextProvider>
  );
};
